import { Component } from "react";
import { withTranslation } from "react-i18next";
import {
	CircularProgress,
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	DialogContentText,
	Snackbar,
} from "@mui/material";
import { JSONTree } from "react-json-tree";
// services
import Gupport from "../../services/gupport";
import Constants from "../../services/constants";
// types
import type { WithTranslation } from "react-i18next";
import type { GatewayId } from "../../types/gateway";
import type { DeviceData } from "../../types/device";
import type { CmdGatewayActionGatewayGetDeviceExtra, CmdGatewayActionMatterReadWildcard } from "../../types/gupport";

type Props = Readonly<WithTranslation & {
	open: boolean;
	isThirdParty: boolean;
	device: DeviceData;
	gatewayId: GatewayId;
	onClose: () => void;
}>;

type State = {
	loading: boolean;
	showCopySnackbar: boolean;
	deviceExtraData: DeviceData | null;
	error: Error | null;
};

class DeviceInfoDialog extends Component<Props, State> {

	public static defaultProps = {
		device: null,
	};

	constructor(props: Props) {
		super(props);

		this.state = {
			loading: false,
			showCopySnackbar: false,
			deviceExtraData: null,
			error: null,
		};

		this.handleSnackbarClose = this.handleSnackbarClose.bind(this);
	}

	override componentDidMount() {
		if (this.props.open && this.props.isThirdParty) {
			if (this.props.device.attributes[Constants.Device.Attributes.Property.DeviceType] === Constants.Device.Attributes.Value.DeviceType.MatterActions) {
				this.fetchMatterStatus();
			} else if (this.props.device.attributes[Constants.Device.Attributes.Property.DeviceType] === Constants.Gateway.Sku.Tuya) {
				this.fetchNativeJson();
			}
		}
	}

	override componentDidUpdate(prevProps) {
		if (!prevProps.open && this.props.open && this.props.isThirdParty) {
			if (this.props.device.attributes[Constants.Device.Attributes.Property.DeviceType] === Constants.Device.Attributes.Value.DeviceType.MatterActions) {
				this.fetchMatterStatus();
			} else if (this.props.device.attributes[Constants.Device.Attributes.Property.DeviceType] === Constants.Gateway.Sku.Tuya) {
				this.fetchNativeJson();
			}
		}
	}

	handleCopyClick(data) {
		const json = JSON.stringify(data, null, 2);
		navigator.clipboard.writeText(json).then(() => {
			this.setState({
				showCopySnackbar: true,
			});
		}, (error) => {
			console.warn(error);
		});
	}

	handleSnackbarClose() {
		this.setState({
			showCopySnackbar: false,
		});
	}

	fetchNativeJson() {
		this.setState({
			loading: true,
		});

		const cmd = {
			action: "gatewayAction",
			module: "gateway",
			function: "get_device_extra",
			params: [{
				id: this.props.device.id,
			}],
			gatewayId: this.props.gatewayId,
		} as const satisfies CmdGatewayActionGatewayGetDeviceExtra;
		Gupport.send(cmd, (error, msg) => {
			if (!error && msg?.payload.status === "ok") {
				this.setState({
					loading: false,
					error: null,
					deviceExtraData: msg.payload.data,
				});
			} else {
				this.setState({
					loading: false,
					error: error,
					deviceExtraData: null,
				});
			}
		});
	}

	fetchMatterStatus() {
		this.setState({
			loading: true,
		});

		const cmd = {
			action: "gatewayAction",
			module: "matter_test_device_talk",
			function: "read_wildcard",
			gatewayId: this.props.gatewayId,
			params: [{
				mac: this.props.device.id,
			}],
			tx_timeout: 300000,
		} as const satisfies CmdGatewayActionMatterReadWildcard;
		Gupport.send(cmd, (error, msg) => {
			if (!error && msg?.payload.status === "ok") {
				this.setState({
					loading: false,
					error: null,
					deviceExtraData: msg.payload.data,
				});
			} else {
				this.setState({
					loading: false,
					error: error,
					deviceExtraData: null,
				});
			}
		}, 60000);
	}

	override render() {
		const { t, open, isThirdParty, device, onClose } = this.props;
		const data = isThirdParty ? this.state.deviceExtraData : device;

		return (
			<>
				<Dialog
					fullWidth={true}
					maxWidth="md"
					open={open}
					onClose={onClose}
				>
					<DialogTitle>{t("gateway.deviceInfoDialogTitle")}</DialogTitle>
					<DialogContent>
						{this.state.loading
							? <CircularProgress style={{ margin: "auto", display: "block" }} />
							: this.state.error ? <DialogContentText>{t("gateway.errorMsg")}</DialogContentText> :
								<>
									<Button variant="contained" onClick={() => (this.handleCopyClick(data))}>
										{t("gateway.copy")}
									</Button>
									{isThirdParty && device.attributes[Constants.Device.Attributes.Property.DeviceType] === Constants.Device.Attributes.Value.DeviceType.MatterActions
										? <JSONTree data={data} />
										: <pre>{JSON.stringify(data, null, 2)}</pre>
									}
								</>
						}
					</DialogContent>
					<DialogActions>
						<Button color="inherit" onClick={onClose}>{t("dialog.close")}</Button>
					</DialogActions>
				</Dialog>
				<Snackbar
					open={this.state.showCopySnackbar}
					message={t("gateway.copyMsg")}
					autoHideDuration={3000}
					onClose={this.handleSnackbarClose}
				/>
			</>
		);
	}

}

export default withTranslation()(DeviceInfoDialog);
